import React, { lazy, Suspense } from 'react'
import { Route, Routes, useLocation } from 'react-router-dom'

import Layout from './components/Layout/Layout'
import HomeSectionsSkeleton from './components/HomeSectionsSkeleton/HomeSectionsSkeleton'

const CompareGlobal = lazy(() => import('./components/CompareGlobal/CompareGlobal'))
const AiAssistantGlobal = lazy(() => import('./components/AiAssistant/AiAssistantGlobal'))

function DeferredCompareGlobal() {
  return (
    <Suspense fallback={null}>
      <CompareGlobal />
    </Suspense>
  )
}

function DeferredAiAssistant() {
  return (
    <Suspense fallback={null}>
      <AiAssistantGlobal />
    </Suspense>
  )
}

const BuyHome = lazy(() => import(/* webpackChunkName: "buy-home" */ './components/BuyHome/BuyHome'))
const LocationSearchPage = lazy(() => import('./components/LocationSearchPage/LocationSearchPage'))
const ExplorePropsPostedByAgentsPage = lazy(() => import('./components/ExplorePropsPostedByAgentsPage/ExplorePropsPostedByAgentsPage'))
const Resale3 = lazy(() => import('./components/RESALE/Resale3/Resale3'))
const ThankForContactingApartments = lazy(() => import('./components/ThankForContactingApartments/ThankForContactingApartments'))
const Compare = lazy(() => import('./components/Compare/Compare'))
const Like = lazy(() => import('./components/Like/Like'))
const LocationPageMap2 = lazy(() => import('./components/LocationPageMap2/LocationPageMap2'))
const ResalePropertyPage = lazy(() => import('./components/RESALE/ResalePropertyPage/ResalePropertyPage'))
const BuyPropertyPageProjects = lazy(() => import('./components/BuyPropertyPageProjects/BuyPropertyPageProjects'))
const RentsHome = lazy(() => import('./components/RENTS/RentsHome/RentsHome'))
const RentsLocationPage = lazy(() => import('./components/RENTS/RentsLocationPage/RentsLocationPage'))
const RentsLocationPageMap1 = lazy(() => import('./components/RENTS/RentsLocationPageMap1/RentsLocationPageMap1'))
const RentsLocationPageMap2 = lazy(() => import('./components/RENTS/RentsLocationPageMap2/RentsLocationPageMap2'))
const RentsLike = lazy(() => import('./components/RENTS/RentsLike/RentsLike'))
const RentsThankForContactingApartments = lazy(() => import('./components/RENTS/RentsThankForContactingApartments/RentsThankForContactingApartments'))
const ThankYouPageRoute = lazy(() => import('./components/ThankYouPageRoute/ThankYouPageRoute'))
const EditInformation = lazy(() => import('./components/EditInformation/EditInformation').then(m => ({ default: m.EditInformation })))
const Notification = lazy(() => import('./components/Notification/Notification').then(m => ({ default: m.Notification })))
const ContactedProperties = lazy(() => import('./components/ContactedProperties/ContactedProperties'))
const SavedProperties = lazy(() => import('./components/SavedProperties/SavedProperties'))
const SavedPropertyNewsPage = lazy(() => import('./components/SavedPropertyNewsPage/SavedPropertyNewsPage'))
const ShortlistedProperties = lazy(() => import('./components/ShortlistedProperties/ShortlistedProperties'))
const ViewedProperties = lazy(() => import('./components/ViewedProperties/ViewedProperties'))
const RecommendedProperties = lazy(() => import('./components/RecommendedProperties/RecommendedProperties'))
const ContactUs = lazy(() => import('./components/ContactUs/ContactUs'))
const CreateAccount = lazy(() => import('./components/CreateAccount/CreateAccount'))
const HotLocalitiesPage = lazy(() => import('./components/HotLocalitiesPage/HotLocalitiesPage'))
const PropertyNewsPage = lazy(() => import('./components/PropertyNewsPage/PropertyNewsPage'))
const Login = lazy(() => import('./components/Login/Login'))
const RentsPropertyPage = lazy(() => import('./components/RENTS/RentsPropertyPage/RentsPropertyPage'))
const ProtectedRoute = lazy(() => import('./components/ProtectedRoute/ProtectedRoute'))
const PropertyPageBuilder = lazy(() => import('./components/PropertyPageBuilder/PropertyPageBuilder'))
const UserLocation = lazy(() => import('./components/UserLocation/UserLocation'))
const AboutCompany = lazy(() => import('./components/AboutCompany/AboutCompany'))
const FAQ = lazy(() => import('./components/FAQ/FAQ'))
const PropertyAssistance = lazy(() => import('./components/PropertyAssistance/PropertyAssistance'))
const PropertyVerification = lazy(() => import('./components/PropertyVerification/PropertyVerification'))
const OtherPropertyServices = lazy(() => import('./components/OtherPropertyServices/OtherPropertyServices'))
const TermsAndConditions = lazy(() => import('./components/TermsAndConditions/TermsAndConditions'))
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy/PrivacyPolicy'))
const HowWeShowListings = lazy(() => import('./components/HowWeShowListings/HowWeShowListings'))

// Wrapper so BuyHome remounts when path changes (fixes stale data when switching Apts/Villas/Plots).
const BuyHomeByRoute = ({ type }) => {
  const { pathname } = useLocation()
  return <BuyHome key={pathname} type={type} />
}

const RouteFallback = () => (
  <div className="min-vh-50 py-8" role="status" aria-label="Loading page">
    <HomeSectionsSkeleton sections={2} />
  </div>
)

const AppRouter = () => {
  return (
    <>
      <DeferredCompareGlobal />
      <DeferredAiAssistant />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route element={<Layout />}>
        <Route path='/' element={<BuyHomeByRoute type="apartment" />}></Route>

        <Route path='/create-account' element={<CreateAccount></CreateAccount>}></Route>
        <Route path='/login' element={<Login></Login>}></Route>

        <Route path='/about-company' element={<AboutCompany></AboutCompany>}></Route>

        <Route path='/like' element={<ProtectedRoute><Like type="apartment" /></ProtectedRoute>} />


        <Route path='/edit-information' element={<EditInformation></EditInformation>}></Route>
        <Route path='/notifications' element={<Notification></Notification>}></Route>
        <Route path='/contacted-properties' element={<ContactedProperties></ContactedProperties>}></Route>
        <Route path='/saved-properties' element={<SavedProperties></SavedProperties>}></Route>
        <Route path='/saved-property-news' element={<SavedPropertyNewsPage />}></Route>
        <Route path='/shortlisted-properties' element={<ShortlistedProperties></ShortlistedProperties>}></Route>
        <Route path='/viewed-properties' element={<ViewedProperties></ViewedProperties>}></Route>
        <Route path='/recommended-properties' element={<RecommendedProperties></RecommendedProperties>}></Route>
        <Route path='/thank-you-page' element={<ThankYouPageRoute />}></Route>
        <Route path='/contact-us' element={<ContactUs></ContactUs>}></Route>
        <Route path='/terms-and-conditions' element={<TermsAndConditions />}></Route>
        <Route path='/privacy-policy' element={<PrivacyPolicy />}></Route>
        <Route path='/faqs' element={<FAQ></FAQ>}></Route>
        <Route path='/how-we-show-listings' element={<HowWeShowListings />}></Route>
        <Route path='/property-assistance' element={<PropertyAssistance></PropertyAssistance>}></Route>
        <Route path='/property-verification' element={<PropertyVerification></PropertyVerification>}></Route>
        <Route path='/other-property-services' element={<OtherPropertyServices></OtherPropertyServices>}></Route>

        <Route path='/rents-home' element={<RentsHome></RentsHome>}></Route>
        <Route path='/rents-location-page' element={<RentsLocationPage />}></Route>
        <Route path='/rents-apts-search-screen' element={<RentsLocationPage type="apartment" />}></Route>
        <Route path='/rents-villas-search-screen' element={<RentsLocationPage type="villa" />}></Route>
        <Route path='/rents-location-page-map1' element={<RentsLocationPageMap1></RentsLocationPageMap1>}></Route>
        <Route path='/rents-location-page-map2' element={<RentsLocationPageMap2></RentsLocationPageMap2>}></Route>
        <Route path='/rents-like' element={<RentsLike></RentsLike>}></Route>
        <Route path='/rents-thank-for-contacting-apartments' element={<RentsThankForContactingApartments></RentsThankForContactingApartments>}></Route>

        <Route path='/rent-apartments-home' element={<RentsHome />}></Route>
        <Route path='/rent-villas-home' element={<RentsHome />}></Route>
        <Route path='/rents-apts-compare' element={<Compare type="apartment" />}></Route>
        <Route path='/rents-villas-compare' element={<Compare type="villa" />}></Route>
        <Route path='/rents-apts-property-page/:slug' element={<RentsPropertyPage type="apartment" />}></Route>
        <Route path='/rents-villas-property-page/:slug' element={<RentsPropertyPage type="villa" />}></Route>


        <Route path='/plots-home' element={<BuyHomeByRoute type="plot" />}></Route>
        <Route path='/plot-projects/:slug' element={<BuyPropertyPageProjects type="plot" />}></Route>
        <Route path='/allplots/:slug' element={<BuyPropertyPageProjects type="plot" />}></Route>
        <Route path='/premiumplots/:slug' element={<BuyPropertyPageProjects type="plot" />}></Route>
        <Route path='/categoryplots/:slug' element={<BuyPropertyPageProjects type="plot" />}></Route>
        <Route path='/mapplots/:slug' element={<BuyPropertyPageProjects type="plot" />}></Route>
        <Route path='/plots-search-screen' element={<LocationSearchPage type="plot" />}></Route>
        <Route path='/plots-resale3' element={<Resale3></Resale3>}></Route>
        <Route path='/plots-explore-properties-posted-by-agents-page' element={<ExplorePropsPostedByAgentsPage></ExplorePropsPostedByAgentsPage>}></Route>
        <Route path='/plots-location-page-map2' element={<LocationPageMap2></LocationPageMap2>}></Route>
        <Route path='/plots-user-location' element={<UserLocation propertyType="plot" />}></Route>
        <Route path='/plots-thank-for-contacting-apartments' element={<ThankForContactingApartments property_type="plot"></ThankForContactingApartments>}></Route>
        <Route path='/plots-compare' element={<Compare type="plot" />}></Route>
        <Route path='/plots-like' element={<Like type="plot" />}></Route>
        <Route path='/plots-resale-property-page' element={<ResalePropertyPage type="plot" />}></Route>
        <Route path='/plots-resale-property-page/:slug' element={<ResalePropertyPage type="plot" />}></Route>

        <Route path='/plots-hot-localities-page/:slug' element={<HotLocalitiesPage type="plot" />}></Route>
        <Route path='/plots-property-news-page/:slug' element={<PropertyNewsPage type="plot" />}></Route>

        <Route path='/villas-home' element={<BuyHomeByRoute type="villa" />}></Route>
        <Route path='/villa-projects/:slug' element={<BuyPropertyPageProjects type="villa" />}></Route>
        <Route path='/allvillas/:slug' element={<BuyPropertyPageProjects type="villa" />}></Route>
        <Route path='/premiumvillas/:slug' element={<BuyPropertyPageProjects type="villa" />}></Route>
        <Route path='/categoryvillas/:slug' element={<BuyPropertyPageProjects type="villa" />}></Route>
        <Route path='/mapvillas/:slug' element={<BuyPropertyPageProjects type="villa" />}></Route>

        <Route path='/villas-search-screen' element={<LocationSearchPage type="villa" />}></Route>
        <Route path='/villas-resale3' element={<Resale3 type="villa" />}></Route>
        <Route path='/villas-explore-properties-posted-by-agents-page' element={<ExplorePropsPostedByAgentsPage type="villa" />}></Route>
        <Route path='/villas-location-page-map2' element={<LocationPageMap2 type="villa" />}></Route>
        <Route path='/villas-thank-for-contacting-apartments' element={<ThankForContactingApartments property_type="villa"></ThankForContactingApartments>}></Route>
        <Route path='/villas-compare' element={<Compare type="villa" />}></Route>
        <Route path='/villas-like' element={<Like type="villa" />}></Route>
        <Route path='/villas-resale-property-page' element={<ResalePropertyPage type="villa" />}></Route>
        <Route path='/villas-resale-property-page/:slug' element={<ResalePropertyPage type="villa" />}></Route>

        <Route path='/villas-hot-localities-page/:slug' element={<HotLocalitiesPage type="villa" />}></Route>
        <Route path='/villas-property-news-page/:slug' element={<PropertyNewsPage type="villa" />}></Route>
        <Route path='/villas-user-location' element={<UserLocation propertyType="villa" />}></Route>

        <Route path='/apts-home' element={<BuyHomeByRoute type="apartment" />}></Route>
        <Route path='/projects/:slug' element={<BuyPropertyPageProjects type="apartment" />} />
        <Route path='/allprojects/:slug' element={<BuyPropertyPageProjects type="apartment" />} />
        <Route path='/premiumprojects/:slug' element={<BuyPropertyPageProjects type="apartment" />} />
        <Route path='/categoryprojects/:slug' element={<BuyPropertyPageProjects type="apartment" />} />
        <Route path='/mapsprojects/:slug' element={<BuyPropertyPageProjects type="apartment" />} />
        <Route path='/apts-projects-by-builder' element={<PropertyPageBuilder type="apartment" />} />
        <Route path='/aptspropertypagebuilder' element={<PropertyPageBuilder type="apartment" />} />
        <Route path='/villas-projects-by-builder' element={<PropertyPageBuilder type="villa" />} />
        <Route path='/plots-projects-by-builder' element={<PropertyPageBuilder type="plot" />} />
        <Route path='/apts-search-screen' element={<LocationSearchPage type="apartment" />}></Route>
        <Route path='/apts-resale3' element={<Resale3 type="apartment" />}></Route>
        <Route path='/apts-explore-properties-posted-by-agents-page' element={<ExplorePropsPostedByAgentsPage type="apartment" />}></Route>
        <Route path='/apts-location-page' element={<LocationPageMap2 type="apartment" />}></Route>
        <Route path='/apts-user-location' element={<UserLocation propertyType="apartment" />}></Route>
        <Route path='/apts-thank-for-contacting-apartments' element={<ThankForContactingApartments property_type="apartment"></ThankForContactingApartments>}></Route>
        <Route path='/apts-compare' element={<Compare type="apartment" />}></Route>
        <Route path='/apts-like' element={<Like type="apartment" />}></Route>
        {/* <Route path='/projects/apartment-buy/{slug}' element={<AptsPropertyPage></AptsPropertyPage>}></Route> */}
        <Route path='/apts-resale-property-page' element={<ResalePropertyPage type="apartment" />}></Route>
        <Route path='/apts-resale-property-page/:slug' element={<ResalePropertyPage type="apartment" />}></Route>


        <Route path="/apts-hot-localities-page/:slug" element={<HotLocalitiesPage type="apartment" />} />
        <Route path='/apts-property-news-page/:slug' element={<PropertyNewsPage type="apartment" />}></Route>

          </Route>
      </Routes>
      </Suspense>
    </>
  )
}

export default AppRouter
